import { useState, useEffect } from "react";
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  User, 
  Users, 
  Heart, 
  FileText, 
  ClipboardList,
  Calendar,
  MapPin,
  Euro,
  Loader2,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function CampRegistrationForm({ selectedCamp, onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [camps, setCamps] = useState([]);
  const [loadingCamps, setLoadingCamps] = useState(true);
  
  const totalSteps = 6;
  
  const [formData, setFormData] = useState({
    camp_id: selectedCamp?.id || "",
    participant: {
      first_name: "",
      last_name: "",
      birth_date: "",
      gender: "",
      jersey_size: "",
      shorts_size: "",
      current_club: "",
      position: "",
      is_goalkeeper: false
    },
    parent: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      street: "",
      house_number: "",
      postal_code: "",
      city: ""
    },
    health: {
      notes: "",
      can_go_home_alone: true,
      pickup_person: "",
      emergency_contact_name: "",
      emergency_contact_phone: ""
    },
    consent: {
      agb_accepted: false,
      privacy_accepted: false,
      marketing_consent: false
    }
  });

  // Fetch available camps
  useEffect(() => {
    const fetchCamps = async () => {
      try {
        const res = await fetch(`${API}/api/camps/available`);
        const data = await res.json();
        setCamps(data.camps || []);
        
        // Auto-select if only one camp or if camp was pre-selected
        if (selectedCamp?.id) {
          setFormData(prev => ({ ...prev, camp_id: selectedCamp.id }));
        } else if (data.camps?.length === 1) {
          setFormData(prev => ({ ...prev, camp_id: data.camps[0].id }));
        }
      } catch (err) {
        console.error("Error fetching camps:", err);
      } finally {
        setLoadingCamps(false);
      }
    };
    fetchCamps();
  }, [selectedCamp]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const selectedCampData = camps.find(c => c.id === formData.camp_id);

  const validateStep = (step) => {
    switch (step) {
      case 1: // Camp selection
        return !!formData.camp_id;
      case 2: // Participant
        const p = formData.participant;
        return p.first_name && p.last_name && p.birth_date && p.gender && p.jersey_size && p.shorts_size;
      case 3: // Parent
        const pa = formData.parent;
        return pa.first_name && pa.last_name && pa.email && pa.phone;
      case 4: // Health
        const h = formData.health;
        return h.emergency_contact_name && h.emergency_contact_phone;
      case 5: // Consent
        return formData.consent.agb_accepted && formData.consent.privacy_accepted;
      case 6: // Summary
        return true;
      default:
        return true;
    }
  };

  const getValidationErrors = (step) => {
    const errors = [];
    switch (step) {
      case 2:
        const p = formData.participant;
        if (!p.first_name) errors.push("Vorname");
        if (!p.last_name) errors.push("Nachname");
        if (!p.birth_date) errors.push("Geburtsdatum");
        if (!p.gender) errors.push("Geschlecht");
        if (!p.jersey_size) errors.push("Trikotgröße");
        if (!p.shorts_size) errors.push("Hosengröße");
        break;
      case 3:
        const pa = formData.parent;
        if (!pa.first_name) errors.push("Vorname");
        if (!pa.last_name) errors.push("Nachname");
        if (!pa.email) errors.push("E-Mail");
        if (!pa.phone) errors.push("Telefon");
        break;
      case 4:
        const h = formData.health;
        if (!h.emergency_contact_name) errors.push("Notfallkontakt Name");
        if (!h.emergency_contact_phone) errors.push("Notfallkontakt Telefon");
        break;
      case 5:
        if (!formData.consent.agb_accepted) errors.push("Camp-Teilnahmebedingungen");
        if (!formData.consent.privacy_accepted) errors.push("Datenschutz");
        break;
    }
    return errors;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setError(null);
    } else {
      const errors = getValidationErrors(currentStep);
      if (errors.length > 0) {
        setError(`Bitte fülle folgende Felder aus: ${errors.join(", ")}`);
      } else {
        setError("Bitte fülle alle Pflichtfelder aus.");
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      setError("Bitte bestätige alle Pflichtfelder.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API}/api/camps/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Registrierung fehlgeschlagen");
      }

      onSuccess(data);
    } catch (err) {
      setError(err.message || "Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { num: 1, label: "Camp", icon: Calendar },
    { num: 2, label: "Teilnehmer", icon: User },
    { num: 3, label: "Eltern", icon: Users },
    { num: 4, label: "Gesundheit", icon: Heart },
    { num: 5, label: "Einverständnis", icon: FileText },
    { num: 6, label: "Übersicht", icon: ClipboardList }
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Camp Selection
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white">
                Wähle dein Camp
              </h3>
              <p className="text-white/60 mt-2">Welches Camp möchtest du besuchen?</p>
            </div>

            {loadingCamps ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#c1ff72]" />
              </div>
            ) : (
              <div className="grid gap-4">
                {camps.map((camp) => (
                  <div
                    key={camp.id}
                    onClick={() => camp.can_register && setFormData(prev => ({ ...prev, camp_id: camp.id }))}
                    className={`relative p-6 rounded-xl cursor-pointer transition-all ${
                      formData.camp_id === camp.id
                        ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]"
                        : !camp.can_register
                        ? "bg-[#1A1A1A]/50 border-2 border-white/5 opacity-60 cursor-not-allowed"
                        : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                    }`}
                  >
                    {camp.is_full && (
                      <div className="absolute top-3 right-3 bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded">
                        Ausgebucht
                      </div>
                    )}
                    {camp.deadline_passed && !camp.is_full && (
                      <div className="absolute top-3 right-3 bg-gray-500/20 text-gray-400 text-xs px-2 py-1 rounded">
                        Anmeldefrist abgelaufen
                      </div>
                    )}
                    {camp.has_early_bird && camp.early_bird_remaining > 0 && camp.can_register && (
                      <div className="absolute top-3 left-3 bg-[#22c55e]/20 text-[#22c55e] text-xs px-2 py-1 rounded">
                        10% Rabatt – Noch {camp.early_bird_remaining} Plätze!
                      </div>
                    )}
                    {formData.camp_id === camp.id && (
                      <div className="absolute top-3 right-3 bg-[#c1ff72] text-black text-xs px-2 py-1 rounded flex items-center gap-1">
                        <Check className="w-3 h-3" /> Ausgewählt
                      </div>
                    )}
                    
                    <h4 className="font-['Bebas_Neue'] text-xl text-white">{camp.name}</h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div className="flex items-center gap-2 text-white/70">
                        <Calendar className="w-4 h-4 text-[#c1ff72]" />
                        <span className="text-sm">{camp.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <MapPin className="w-4 h-4 text-[#c1ff72]" />
                        <span className="text-sm">{camp.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <Euro className="w-4 h-4 text-[#c1ff72]" />
                        {camp.has_early_bird && camp.early_bird_remaining > 0 && camp.can_register ? (
                          <span className="text-sm">
                            <span className="line-through text-white/40">{camp.original_price || camp.price}€</span>
                            <span className="ml-1 font-semibold text-[#22c55e]">{camp.current_price}€</span>
                          </span>
                        ) : (
                          <span className="text-sm font-semibold">{camp.current_price || camp.price}€</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <Users className="w-4 h-4 text-[#c1ff72]" />
                        <span className="text-sm">
                          {camp.remaining_spots > 0 
                            ? `Noch ${camp.remaining_spots} Plätze` 
                            : "Ausgebucht"}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between mt-3">
                      <p className="text-white/50 text-sm">{camp.age_range}</p>
                      {camp.registration_deadline && camp.can_register && (
                        <p className="text-white/40 text-xs">Anmeldeschluss: {camp.registration_deadline}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 2: // Participant
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center justify-center gap-2">
                <User className="w-6 h-6 text-[#c1ff72]" />
                Teilnehmer
              </h3>
              <p className="text-white/60 mt-1">Daten des Kindes</p>
            </div>

            <div className="space-y-5">
              <div className="bg-[#1A1A1A] rounded-xl p-5">
                <h4 className="text-white/80 text-sm font-medium mb-4 uppercase tracking-wide">Persönliche Daten</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">Vorname *</Label>
                    <Input
                      value={formData.participant.first_name}
                      onChange={(e) => updateField("participant", "first_name", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Nachname *</Label>
                    <Input
                      value={formData.participant.last_name}
                      onChange={(e) => updateField("participant", "last_name", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      autoComplete="family-name"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Geburtsdatum *</Label>
                    <Input
                      type="date"
                      value={formData.participant.birth_date}
                      onChange={(e) => updateField("participant", "birth_date", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Geschlecht *</Label>
                    <Select
                      value={formData.participant.gender}
                      onValueChange={(value) => updateField("participant", "gender", value)}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="male">Männlich</SelectItem>
                        <SelectItem value="female">Weiblich</SelectItem>
                        <SelectItem value="diverse">Divers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="bg-[#1A1A1A] rounded-xl p-5">
                <h4 className="text-white/80 text-sm font-medium mb-4 uppercase tracking-wide">Ausrüstung</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">Trikotgröße *</Label>
                    <Select
                      value={formData.participant.jersey_size}
                      onValueChange={(value) => updateField("participant", "jersey_size", value)}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {["116", "122", "128", "134", "140", "146", "152", "158", "164", "S", "M", "L"].map(size => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70">Hosengröße *</Label>
                    <Select
                      value={formData.participant.shorts_size}
                      onValueChange={(value) => updateField("participant", "shorts_size", value)}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {["116", "122", "128", "134", "140", "146", "152", "158", "164", "S", "M", "L"].map(size => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="bg-[#1A1A1A] rounded-xl p-5">
                <h4 className="text-white/80 text-sm font-medium mb-4 uppercase tracking-wide">Fußball (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">Aktueller Verein</Label>
                    <Input
                      value={formData.participant.current_club}
                      onChange={(e) => updateField("participant", "current_club", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="z.B. SC Drolshagen"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Position</Label>
                    <Select
                      value={formData.participant.position}
                      onValueChange={(value) => updateField("participant", "position", value)}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue placeholder="Optional" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="sturm">Sturm</SelectItem>
                        <SelectItem value="mittelfeld">Mittelfeld</SelectItem>
                        <SelectItem value="verteidigung">Verteidigung</SelectItem>
                        <SelectItem value="torwart">Torwart</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <Checkbox
                    id="is_goalkeeper"
                    checked={formData.participant.is_goalkeeper}
                    onCheckedChange={(checked) => updateField("participant", "is_goalkeeper", checked)}
                    className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72]"
                  />
                  <Label htmlFor="is_goalkeeper" className="text-white/70 cursor-pointer">
                    Mein Kind ist Torwart
                  </Label>
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Parent
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center justify-center gap-2">
                <Users className="w-6 h-6 text-[#c1ff72]" />
                Eltern / Erziehungsberechtigte
              </h3>
              <p className="text-white/60 mt-1">Kontaktdaten für Rückfragen</p>
            </div>

            <div className="bg-[#1A1A1A] rounded-xl p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Vorname *</Label>
                  <Input
                    value={formData.parent.first_name}
                    onChange={(e) => updateField("parent", "first_name", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Nachname *</Label>
                  <Input
                    value={formData.parent.last_name}
                    onChange={(e) => updateField("parent", "last_name", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    autoComplete="family-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">E-Mail *</Label>
                  <Input
                    type="email"
                    value={formData.parent.email}
                    onChange={(e) => updateField("parent", "email", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Telefon *</Label>
                  <Input
                    type="tel"
                    value={formData.parent.phone}
                    onChange={(e) => updateField("parent", "phone", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                <p className="text-white/50 text-sm mb-4">Adresse (optional)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label className="text-white/70">Straße</Label>
                    <Input
                      value={formData.parent.street}
                      onChange={(e) => updateField("parent", "street", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      autoComplete="address-line1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Hausnr.</Label>
                    <Input
                      value={formData.parent.house_number}
                      onChange={(e) => updateField("parent", "house_number", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label className="text-white/70">PLZ</Label>
                    <Input
                      value={formData.parent.postal_code}
                      onChange={(e) => updateField("parent", "postal_code", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      autoComplete="postal-code"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-white/70">Ort</Label>
                    <Input
                      value={formData.parent.city}
                      onChange={(e) => updateField("parent", "city", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      autoComplete="address-level2"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Health
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center justify-center gap-2">
                <Heart className="w-6 h-6 text-[#c1ff72]" />
                Gesundheit & Organisation
              </h3>
              <p className="text-white/60 mt-1">Wichtige Informationen für das Camp</p>
            </div>

            <div className="bg-[#1A1A1A] rounded-xl p-5 space-y-5">
              <div>
                <Label className="text-white/70">
                  Allergien, Medikamente, Verletzungen oder wichtige Hinweise
                </Label>
                <Textarea
                  value={formData.health.notes}
                  onChange={(e) => updateField("health", "notes", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1 min-h-[100px]"
                  placeholder="z.B. Laktoseintoleranz, nimmt regelmäßig Medikamente..."
                />
              </div>

              <div className="pt-4 border-t border-white/10">
                <Label className="text-white/70 block mb-3">
                  Darf dein Kind nach dem Camp alleine nach Hause?
                </Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="can_go_home"
                      checked={formData.health.can_go_home_alone === true}
                      onChange={() => updateField("health", "can_go_home_alone", true)}
                      className="accent-[#c1ff72]"
                    />
                    <span className="text-white">Ja</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="can_go_home"
                      checked={formData.health.can_go_home_alone === false}
                      onChange={() => updateField("health", "can_go_home_alone", false)}
                      className="accent-[#c1ff72]"
                    />
                    <span className="text-white">Nein</span>
                  </label>
                </div>
                
                {!formData.health.can_go_home_alone && (
                  <div className="mt-4">
                    <Label className="text-white/70">Wer holt das Kind ab?</Label>
                    <Input
                      value={formData.health.pickup_person}
                      onChange={(e) => updateField("health", "pickup_person", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="Name der abholenden Person"
                    />
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-white/10">
                <h4 className="text-white font-medium mb-4">Notfallkontakt *</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">Name *</Label>
                    <Input
                      value={formData.health.emergency_contact_name}
                      onChange={(e) => updateField("health", "emergency_contact_name", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="Name des Notfallkontakts"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Telefon *</Label>
                    <Input
                      type="tel"
                      value={formData.health.emergency_contact_phone}
                      onChange={(e) => updateField("health", "emergency_contact_phone", e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="Telefonnummer"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5: // Consent
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center justify-center gap-2">
                <FileText className="w-6 h-6 text-[#c1ff72]" />
                Einverständnis
              </h3>
              <p className="text-white/60 mt-1">Rechtliche Zustimmungen</p>
            </div>

            <div className="bg-[#1A1A1A] rounded-xl p-5 space-y-5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agb"
                  checked={formData.consent.agb_accepted}
                  onCheckedChange={(checked) => updateField("consent", "agb_accepted", checked)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72]"
                />
                <div>
                  <Label htmlFor="agb" className="text-white cursor-pointer">
                    Ich akzeptiere die Camp-Teilnahmebedingungen *
                  </Label>
                  <p className="text-white/50 text-sm mt-1">
                    Ich habe die{" "}
                    <a href="/camp-teilnahmebedingungen" target="_blank" rel="noopener noreferrer" className="text-[#c1ff72] underline">
                      Camp-Teilnahmebedingungen
                    </a>{" "}
                    gelesen und akzeptiere diese.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={formData.consent.privacy_accepted}
                  onCheckedChange={(checked) => updateField("consent", "privacy_accepted", checked)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72]"
                />
                <div>
                  <Label htmlFor="privacy" className="text-white cursor-pointer">
                    Ich habe die Datenschutzerklärung gelesen *
                  </Label>
                  <p className="text-white/50 text-sm mt-1">
                    Ich stimme der Verarbeitung meiner Daten gemäß der{" "}
                    <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#c1ff72] underline">
                      Datenschutzerklärung
                    </a>{" "}
                    zu.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="marketing"
                    checked={formData.consent.marketing_consent}
                    onCheckedChange={(checked) => updateField("consent", "marketing_consent", checked)}
                    className="mt-1 border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72]"
                  />
                  <div>
                    <Label htmlFor="marketing" className="text-white cursor-pointer">
                      Newsletter & Updates (optional)
                    </Label>
                    <p className="text-white/50 text-sm mt-1">
                      Ich möchte über zukünftige Camps und Veranstaltungen informiert werden.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 6: // Summary
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center justify-center gap-2">
                <ClipboardList className="w-6 h-6 text-[#c1ff72]" />
                Zusammenfassung
              </h3>
              <p className="text-white/60 mt-1">Bitte überprüfe deine Angaben</p>
            </div>

            <div className="space-y-4">
              {/* Camp */}
              <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 rounded-xl p-5">
                <h4 className="font-['Bebas_Neue'] text-lg text-[#c1ff72] mb-3">Camp</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-white/50">Camp:</span>
                    <p className="text-white">{selectedCampData?.name}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Datum:</span>
                    <p className="text-white">{selectedCampData?.date}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Ort:</span>
                    <p className="text-white">{selectedCampData?.location}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Uhrzeit:</span>
                    <p className="text-white">{selectedCampData?.time || "täglich 10:00 – 14:00 Uhr"}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-white/50">Preis:</span>
                    {selectedCampData?.has_early_bird && selectedCampData?.early_bird_remaining > 0 ? (
                      <div className="flex items-center gap-3">
                        <p className="text-white/50 line-through">{selectedCampData?.original_price || selectedCampData?.price}€</p>
                        <p className="text-[#22c55e] font-bold">{selectedCampData?.current_price}€</p>
                        <span className="bg-[#22c55e]/20 text-[#22c55e] text-xs px-2 py-0.5 rounded">10% Frühbucher</span>
                      </div>
                    ) : (
                      <p className="text-white font-bold">{selectedCampData?.current_price || selectedCampData?.price}€</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Participant */}
              <div className="bg-[#1A1A1A] rounded-xl p-5">
                <h4 className="font-['Bebas_Neue'] text-lg text-white mb-3">Teilnehmer</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-white/50">Name:</span>
                    <p className="text-white">{formData.participant.first_name} {formData.participant.last_name}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Geburtsdatum:</span>
                    <p className="text-white">{formData.participant.birth_date}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Trikot / Hose:</span>
                    <p className="text-white">{formData.participant.jersey_size} / {formData.participant.shorts_size}</p>
                  </div>
                  {formData.participant.current_club && (
                    <div>
                      <span className="text-white/50">Verein:</span>
                      <p className="text-white">{formData.participant.current_club}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Parent */}
              <div className="bg-[#1A1A1A] rounded-xl p-5">
                <h4 className="font-['Bebas_Neue'] text-lg text-white mb-3">Eltern</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-white/50">Name:</span>
                    <p className="text-white">{formData.parent.first_name} {formData.parent.last_name}</p>
                  </div>
                  <div>
                    <span className="text-white/50">E-Mail:</span>
                    <p className="text-white">{formData.parent.email}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Telefon:</span>
                    <p className="text-white">{formData.parent.phone}</p>
                  </div>
                </div>
              </div>

              {/* Health */}
              <div className="bg-[#1A1A1A] rounded-xl p-5">
                <h4 className="font-['Bebas_Neue'] text-lg text-white mb-3">Notfallkontakt</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-white/50">Name:</span>
                    <p className="text-white">{formData.health.emergency_contact_name}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Telefon:</span>
                    <p className="text-white">{formData.health.emergency_contact_phone}</p>
                  </div>
                </div>
                {formData.health.notes && (
                  <div className="mt-3">
                    <span className="text-white/50 text-sm">Hinweise:</span>
                    <p className="text-white text-sm">{formData.health.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl my-8">
        {/* Close Button - positioned outside the stepper area */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 md:top-2 md:right-2 w-10 h-10 flex items-center justify-center bg-[#1A1A1A] border border-white/20 rounded-full text-white/70 hover:text-white hover:bg-[#2A2A2A] transition-colors z-20"
          aria-label="Schließen"
          data-testid="camp-form-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress Steps */}
        <div className="px-6 pt-8 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    currentStep > step.num
                      ? "bg-[#c1ff72] text-black"
                      : currentStep === step.num
                      ? "bg-[#c1ff72]/20 text-[#c1ff72] border-2 border-[#c1ff72]"
                      : "bg-white/5 text-white/40"
                  }`}
                >
                  {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-6 md:w-12 h-0.5 mx-1 ${
                    currentStep > step.num ? "bg-[#c1ff72]" : "bg-white/10"
                  }`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-white/60 text-sm mt-2">
            Schritt {currentStep} von {totalSteps}: {steps[currentStep - 1]?.label}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="px-6 py-4 border-t border-white/10 flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="border-white/20 text-white hover:bg-white/5 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={nextStep}
              className="bg-[#c1ff72] text-black hover:bg-[#d4ff9a] flex-1 max-w-[200px]"
            >
              Weiter
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#c1ff72] text-black hover:bg-[#d4ff9a] flex-1 max-w-[250px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Jetzt verbindlich anmelden
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
